import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type HorasExtras as Dados } from "../api";

/**
 * MINHAS HORAS EXTRAS — o que o DP lançou para pagamento.
 *
 * Pela regra da casa, vão para cá as horas de FIM DE SEMANA e FERIADO (e o que
 * o DP decidiu pagar). O excedente de dia comum fica no banco de horas — e a
 * tela diz isso, porque é a pergunta que todo mundo faz quando a hora extra da
 * terça-feira "não aparece".
 *
 * O valor vai junto: é dinheiro da pessoa, e ela confere no holerite.
 */

const MESES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

function horas(v: number): string {
  const total = Math.round(v * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
}

function reais(v: number | null): string {
  if (v == null) return "—";
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function data(iso: string): string {
  return iso.split("-").reverse().join("/");
}

export default function HorasExtras() {
  const [ano, setAno] = useState(new Date().getFullYear());
  const [dados, setDados] = useState<Dados | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      setDados(await api.horasExtras(ano));
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível carregar as horas extras.");
    }
  }, [ano]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // O ano corrente sempre aparece, mesmo sem lançamento — senão o seletor
  // sumiria justamente para quem ainda não fez hora extra no ano.
  const anos = [...new Set([new Date().getFullYear(), ...(dados?.anos ?? [])])].sort((a, b) => b - a);

  return (
    <div className="pagina">
      <p className="explica">
        Aqui ficam as horas extras lançadas para pagamento — fim de semana e feriado. O que passa da
        jornada em dia comum vai para o <strong>banco de horas</strong>.
      </p>

      <div className="linha-filtro">
        <label className="campo campo-inline">
          <span>Ano</span>
          <select value={ano} onChange={(e) => setAno(Number(e.target.value))}>
            {anos.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>
      </div>

      {erro && <div className="aviso-erro">{erro}</div>}

      {!dados ? (
        <div className="carregando">Carregando…</div>
      ) : dados.lancamentos.length === 0 ? (
        <div className="vazio">Nenhuma hora extra lançada em {ano}.</div>
      ) : (
        <>
          <div className="resumo">
            <div className="resumo-item">
              <span>Total no ano</span>
              <strong>{horas(dados.total.horas)}</strong>
            </div>
            <div className="resumo-item">
              <span>Valor no ano</span>
              <strong>{reais(dados.total.valor)}</strong>
            </div>
          </div>

          {dados.meses.map((m) => (
            <section key={m.mes} className="grupo">
              <header className="grupo-topo">
                <span className="grupo-titulo">{MESES[Number(m.mes.slice(5)) - 1]}</span>
                <span className="grupo-total">
                  {horas(m.horas)}
                  {m.valor > 0 ? ` · ${reais(m.valor)}` : ""}
                </span>
              </header>
              <ul className="lista">
                {dados.lancamentos
                  .filter((l) => l.data.startsWith(m.mes))
                  .map((l, i) => (
                    <li key={`${l.data}-${i}`} className="item">
                      <div className="item-linha">
                        <span className="item-principal">{data(l.data)}</span>
                        <strong className="item-valor">{horas(l.horas)}</strong>
                      </div>
                      <div className="item-linha item-detalhe">
                        <span>{l.tipo ?? "Hora extra"}</span>
                        <span>
                          {l.status ?? ""}
                          {l.valor != null && l.valor > 0 ? ` · ${reais(l.valor)}` : ""}
                        </span>
                      </div>
                    </li>
                  ))}
              </ul>
            </section>
          ))}
        </>
      )}
    </div>
  );
}
